import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useState, type ReactNode } from 'react'
import { api, type Node, type MetricPoint, type NodeInventory, humanBytes, timeAgo } from '../lib/api'
import { Card, Spinner, Badge, Button, Modal, Field, Input } from '../components/ui'
import { IconCircleDot } from '../components/icons'
import { MetricsChart } from '../components/MetricsChart'
import { ActiveTaskBadge } from '../components/ActiveTaskBadge'
import { PHPManager } from '../components/PHPManager'
import { NodeSystemBlock } from '../components/NodeSystem'
import { RowMenu } from '../components/RowMenu'
import { SecurityBlock } from '../components/SecurityBlock'

interface NodeSummary {
  clients: number
  sites: number
  databases: number
  ftp_users: number
  per_client: {
    id: number
    unix_user: string
    full_name: string
    sites: number
    databases: number
    ftp_users: number
    disk_kb: number
  }[]
}

const STATUS_RU: Record<string, string> = {
  online: 'онлайн', registered: 'подключена', installing: 'установка',
  pending: 'ожидание', failed: 'ошибка', offline: 'офлайн',
}
const STATUS_TONE: Record<string, 'green'|'blue'|'yellow'|'red'|'gray'> = {
  online: 'green', registered: 'blue', installing: 'blue',
  pending: 'gray', failed: 'red', offline: 'gray',
}

const PERIODS = [
  { k: '1h',  label: '1 час',  sinceMin: 60,     bucket: 60 },
  { k: '6h',  label: '6 часов', sinceMin: 360,    bucket: 300 },
  { k: '24h', label: '24 часа', sinceMin: 1440,   bucket: 900 },
  { k: '7d',  label: '7 дней',  sinceMin: 10080,  bucket: 3600 },
] as const

export function NodeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [period, setPeriod] = useState<typeof PERIODS[number]>(PERIODS[0])

  const { data: n, isLoading } = useQuery({
    queryKey: ['node', id],
    queryFn: async () => (await api.get<Node>(`/nodes/${id}`)).data,
    refetchInterval: 5000,
  })
  const nav = useNavigate()
  const qc = useQueryClient()
  const [sshOpen, setSshOpen] = useState(false)

  const { data: av } = useQuery({
    queryKey: ['agent-version'],
    queryFn: async () => (await api.get<{ latest: string }>('/agent-version')).data,
    refetchInterval: 60000,
  })

  const { data: inv } = useQuery({
    queryKey: ['node-inventory', id],
    queryFn: async () => (await api.get<NodeInventory>(`/nodes/${id}/inventory`)).data,
    refetchInterval: 15000,
    enabled: !!id,
  })
  const { data: summary } = useQuery({
    queryKey: ['node-summary', id],
    queryFn: async () => (await api.get<NodeSummary>(`/nodes/${id}/summary`)).data,
    refetchInterval: 30000,
    enabled: !!id,
  })

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/nodes/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nodes'] }); nav('/nodes') },
  })

  const { data: metrics = [] } = useQuery({
    queryKey: ['node-metrics', id, period.k],
    queryFn: async () => (await api.get<MetricPoint[]>(
      `/nodes/${id}/metrics?since=${period.sinceMin}&bucket=${period.bucket}`)).data,
    refetchInterval: 15000,
    enabled: !!id,
  })

  if (isLoading || !n) return <div className="flex items-center gap-2 text-neutral-500"><Spinner /> Загрузка…</div>

  const fields: [string, any][] = [
    ['Хост', n.hostname],
    ['IP-адрес', <span className="font-mono">{n.ip_address}</span>],
    ['Операционная система', n.os_name || '—'],
    ['Ядро', <span className="font-mono">{n.kernel || '—'}</span>],
    ['Архитектура', n.arch || '—'],
    ['Ресурсы', n.cpu_count ? `${n.cpu_count} vCPU · ${humanBytes(n.mem_bytes)} · ${humanBytes(n.disk_bytes)}` : '—'],
    ['Версия агента',
      <span className="font-mono flex items-center gap-2">
        {n.agent_version || '—'}
        {av && n.agent_version && n.agent_version !== av.latest && (
          <Badge tone="yellow">устарел · есть {av.latest}</Badge>
        )}
      </span>],
    ['Последний пинг', timeAgo(n.last_heartbeat_at)],
    ['Создана', new Date(n.created_at).toLocaleString('ru-RU')],
  ]

  return (
    <>
      <div className="mb-5">
        <Link to="/nodes" className="text-[13px] text-neutral-500 hover:text-neutral-900">← Ноды</Link>
        <div className="flex items-center gap-3 mt-2">
          <h1 className="text-2xl font-semibold tracking-tight">{n.name}</h1>
          <span className="inline-flex items-center gap-1.5 text-[13px] text-neutral-700">
            <IconCircleDot tone={STATUS_TONE[n.status] || 'gray'} />
            {STATUS_RU[n.status] || n.status}
          </span>
          {id && <ActiveTaskBadge nodeId={id} variant="chip" />}
          <div className="ml-auto flex gap-2">
            <Link to={`/nodes/${id}/terminal`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-md bg-black text-white hover:bg-neutral-800 transition">
              <span className="font-mono">$_</span> Терминал
            </Link>
            <RowMenu items={[
              { label: '🔑 SSH-доступ', onClick: () => setSshOpen(true) },
              { label: '🔍 Пересканировать IP', onClick: () => id && api.post(`/nodes/${id}/ip-addresses/discover`).then((r: any) => alert('Найдено: ' + r.data.count + ' новых')) },
              { label: '↻ Обновить агента', onClick: () => id && api.post(`/nodes/${id}/agent-update`).then(() => alert('Обновление запущено (через SSH)')) },
              { divider: true, label: '', onClick: () => {} },
              { label: '🗑 Удалить ноду', danger: true,
                onClick: () => confirm(`Удалить ноду ${n.name}?`) && del.mutate() },
            ]} />
          </div>
        </div>
        {n.status_message && <p className="text-[13px] text-neutral-500 mt-1">{n.status_message}</p>}
      </div>

      {/* Блок Сводка */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatCard label="Клиенты" value={summary.clients} to="/clients" />
          <StatCard label="Сайты"    value={summary.sites}   to="/sites" />
          <StatCard label="Базы данных" value={summary.databases} to="/databases" />
          <StatCard label="FTP"        value={summary.ftp_users} to="/ftp" />
        </div>
      )}

      {/* Топ клиентов */}
      {summary && summary.per_client.length > 0 && (
        <Card className="mb-5 overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-100">
            <h2 className="text-[15px] font-semibold">Потребление по клиентам</h2>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50/60 border-b border-neutral-100">
              <tr className="text-[11px] uppercase text-neutral-500 tracking-wider">
                <th className="text-left font-medium px-5 py-2">Unix user</th>
                <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Имя</th>
                <th className="text-right font-medium px-4 py-2">Сайты</th>
                <th className="text-right font-medium px-4 py-2">БД</th>
                <th className="text-right font-medium px-4 py-2">FTP</th>
                <th className="text-right font-medium px-5 py-2">Диск</th>
              </tr>
            </thead>
            <tbody>
              {summary.per_client.map(c => (
                <tr key={c.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                  <td className="px-5 py-2.5 font-mono text-[12px]">{c.unix_user}</td>
                  <td className="px-4 py-2.5 text-neutral-600 hidden sm:table-cell">{c.full_name || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-right">{c.sites}</td>
                  <td className="px-4 py-2.5 text-right">{c.databases}</td>
                  <td className="px-4 py-2.5 text-right">{c.ftp_users}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-[12px]">{humanBytes(c.disk_kb * 1024)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Переключатели периода */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-semibold text-neutral-900">Мониторинг</h2>
        <div className="inline-flex bg-white border border-neutral-200 rounded-md overflow-hidden">
          {PERIODS.map((p) => (
            <button
              key={p.k}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-[12px] border-r border-neutral-200 last:border-0 transition ${
                period.k === p.k
                  ? 'bg-neutral-900 text-white'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Системный блок: uptime / топ процессов / TCP / лиснеры */}
      {id && <NodeSystemBlock nodeId={id} />}

      {/* PHP-менеджер */}
      {id && <PHPManager nodeId={id} />}

      {/* Безопасность — Fail2ban */}
      {id && <SecurityBlock nodeId={id} />}

      {/* Установленные компоненты (только просмотр) */}
      <Card className="mb-5 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-[15px] font-semibold">Установленный софт</h2>
            <div className="text-[12px] text-neutral-500 mt-0.5">
              Чтобы что-то добавить или убрать — измени <Link to="/settings" className="underline hover:text-neutral-900">Настройки</Link>
            </div>
          </div>
          {!inv && <span className="text-[12px] text-neutral-400">загрузка…</span>}
        </div>
        {inv && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FeatureRow
              title="Nginx"
              installed={inv.nginx}
              version={inv.nginx_version}
              extra={inv.php_versions.length > 0 && <div className="flex flex-wrap gap-1 mt-1">
                {inv.php_versions.map(v => <Badge key={v} tone="purple">PHP {v}</Badge>)}
              </div>}
            />
            <FeatureRow
              title={inv.mariadb ? 'MariaDB' : 'MySQL'}
              installed={inv.mysql || inv.mariadb}
              version={inv.mysql_version}
            />
            <FeatureRow
              title="SFTP (OpenSSH chroot)"
              installed={inv.sftp && inv.sftp_group}
              note={inv.sftp && !inv.sftp_group ? 'группа beebro-sftp не создана' : undefined}
            />
            <FeatureRow title="Certbot (Let’s Encrypt)" installed={inv.certbot} />
            <FeatureRow title="Postfix" installed={inv.postfix} />
            <FeatureRow title="Dovecot" installed={inv.dovecot} />
            <FeatureRow title="Docker" installed={inv.docker} />
            <FeatureRow title="Redis" installed={inv.redis} />
            <FeatureRow title="Neon (self-hosted Postgres)" installed={inv.neon} />
            {inv.nodejs_version && <FeatureRow title="Node.js" installed={true} version={inv.nodejs_version} />}
            {inv.python3_version && <FeatureRow title="Python 3" installed={true} version={inv.python3_version} />}
          </div>
        )}
      </Card>

      {/* 6 графиков */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <MetricsChart points={metrics} metric="cpu"    title="CPU" />
        <MetricsChart points={metrics} metric="mem"    title="Память" />
        <MetricsChart points={metrics} metric="disk"   title="Диск" />
        <MetricsChart points={metrics} metric="net"    title="Сеть (RX+TX)" />
        <MetricsChart points={metrics} metric="load"   title="Load Average (1m)" />
        <MetricsChart points={metrics} metric="iowait" title="IO Wait" />
      </div>

      {sshOpen && n && <NodeSSHModal node={n} onClose={() => setSshOpen(false)} onSaved={() => setSshOpen(false)} />}

      {/* Свойства */}
      <Card className="divide-y divide-neutral-100">
        {fields.map(([k, v]) => (
          <div key={k} className="grid grid-cols-3 gap-3 px-5 py-3 text-[13px]">
            <div className="text-neutral-500">{k}</div>
            <div className="col-span-2 text-neutral-900">{v}</div>
          </div>
        ))}
      </Card>
    </>
  )
}

function FeatureRow({ title, installed, version, note, extra }: {
  title: string
  installed: boolean
  version?: string
  note?: string
  extra?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 border border-neutral-200 rounded-md bg-neutral-50/40">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${installed ? 'bg-green-500' : 'bg-neutral-300'}`} />
          <span className="text-[13px] font-medium">{title}</span>
          {version && <span className="text-[11px] text-neutral-500 font-mono">{version}</span>}
        </div>
        {note && <div className="text-[11px] text-amber-600 mt-1">{note}</div>}
        {extra}
      </div>
      <span className={`text-[11px] shrink-0 ${installed ? 'text-green-700' : 'text-neutral-400'}`}>
        {installed ? 'установлен' : 'нет'}
      </span>
    </div>
  )
}

function StatCard({ label, value, to }: { label: string; value: number | string; to?: string }) {
  const inner = (
    <div className="p-4 border border-neutral-200 rounded-md bg-white hover:border-neutral-400 transition">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-[22px] font-semibold mt-1 leading-none">{value}</div>
    </div>
  )
  return to ? <Link to={to} className="block">{inner}</Link> : inner
}

function NodeSSHModal({ node, onClose, onSaved }: { node: Node; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ ssh_user: node.ssh_user || 'root', ssh_port: node.ssh_port || 22, ssh_password: '' })
  const [err, setErr] = useState('')
  const save = useMutation({
    mutationFn: async () => (await api.put(`/nodes/${node.id}/ssh`, f)).data,
    onSuccess: onSaved,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title={`SSH-доступ: ${node.name}`}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate() }} className="space-y-4">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-[12px] text-blue-900">
          Пароль нужен для веб-терминала и автообновления агента. Хранится на мастере (шифрованно).
          Оставьте поле пустым чтобы не менять.
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Пользователь"><Input value={f.ssh_user} onChange={e => setF({ ...f, ssh_user: e.target.value })} /></Field>
          <Field label="Порт"><Input type="number" value={f.ssh_port} onChange={e => setF({ ...f, ssh_port: +e.target.value })} /></Field>
          <div />
        </div>
        <Field label="Новый пароль">
          <Input type="password" value={f.ssh_password} onChange={e => setF({ ...f, ssh_password: e.target.value })}
                 placeholder="оставьте пустым чтобы не менять" />
        </Field>
        {err && <div className="text-red-600 text-[13px]">{err}</div>}
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Сохраняем…' : 'Сохранить'}</Button>
        </div>
      </form>
    </Modal>
  )
}
